import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, CheckCheck, Inbox } from "lucide-react";

const NOTIF_LABELS = {
  mission_accepted: "Mission acceptée",
  mission_nearby: "🔔 Mission près de vous",
  mission_cancelled: "Mission annulée",
  payment_received: "Paiement reçu",
  payment_released: "Paiement libéré",
  status_update: "Mise à jour mission",
  kyc_validated: "KYC validé",
  new_message: "Nouveau message",
  dispute_opened: "Litige ouvert",
  dispute_response: "Réponse litige",
  dispute_resolved: "Litige tranché",
};

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "à l'instant";
  if (sec < 3600) return `il y a ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `il y a ${Math.floor(sec / 3600)} h`;
  return `il y a ${Math.floor(sec / 86400)} j`;
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [wobble, setWobble] = useState(false);
  const lastUnreadRef = React.useRef(0);
  const firstLoadRef = React.useRef(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const fresh = data || [];
      const newUnread = fresh.filter((n) => !n.read).length;
      // Wobble UNIQUEMENT si l'unread count augmente DURANT la session,
      // pas au tout premier chargement (sinon ça wobble à chaque reload).
      if (!firstLoadRef.current && newUnread > lastUnreadRef.current) {
        setWobble(true);
        setTimeout(() => setWobble(false), 900);
      }
      lastUnreadRef.current = newUnread;
      firstLoadRef.current = false;
      setItems(fresh);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    // Polling toutes les 15s pour récupérer rapidement les missions proches
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    if (unread === 0) return;
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_) { /* silent */ }
  };

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); }
      catch (_) { /* silent */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.mission_id) navigate(`/missions/${n.mission_id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-[#1F4032]/10 transition-colors"
          aria-label="Notifications"
          data-testid="notif-bell-btn"
        >
          <Bell className={`w-5 h-5 text-[#1F4032] ${wobble ? "bell-wobble" : ""}`} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center font-mono count-pop"
              data-testid="notif-bell-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 max-h-[480px] overflow-hidden flex flex-col modal-zoom-in"
        data-testid="notif-bell-popover"
      >
        <div className="flex items-center justify-between p-3 border-b border-neutral-200 bg-[#FBF7F1]">
          <div className="font-display font-bold text-sm tracking-tight">Notifications</div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-[#1F4032] hover:underline flex items-center gap-1 font-medium"
              data-testid="notif-mark-all-read"
            >
              <CheckCheck className="w-3 h-3" /> Tout marquer lu
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {items.length === 0 ? (
            <div className="p-10 text-center text-neutral-500" data-testid="notif-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2 text-neutral-300" strokeWidth={1.5} />
              <div className="text-sm">Aucune notification</div>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                className={`w-full text-left px-3 py-3 border-b border-neutral-100 last:border-0 hover:bg-[#1F4032]/5 transition-colors block ${!n.read ? "bg-[#1F4032]/[0.03]" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && (
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#1F4032] shrink-0" />
                  )}
                  <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                    <div className="text-xs font-mono text-neutral-500 uppercase tracking-wide">
                      {NOTIF_LABELS[n.type] || n.type}
                    </div>
                    <div className="text-sm text-neutral-800 mt-0.5">{n.text}</div>
                    <div className="text-[11px] text-neutral-400 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
